/**
 * CheckoutOverlay - the post-confirm overlay (P5-D17-F9 §5, T-F9-6).
 *
 * `confirming` = Frame 4 (green check, "Payment complete", the paid amount,
 * "Confirming your booking…") shown while the status poll runs. `slow` = Frame 7
 * (after ~10s without convergence): the headline STAYS "Payment complete" (the
 * charge succeeded — only the booking confirm lags), an amber spinner-clock, the
 * reassurance copy, and a working **Done** (Telegram close). Never a failure tone.
 */

import styles from './CheckoutOverlay.module.css';

/** The overlay's words, which the studio website's computer payment step draws in its own look too. */
export const CHECKOUT_CONFIRM_WORDS = {
  title: 'Payment complete',
  confirming: 'Confirming your booking…',
  slow: "Confirming your booking is taking a moment. We'll message you when it's confirmed.",
} as const;

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function SpinnerClockIcon() {
  return (
    <svg className={styles.spin} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-6.2-8.56" />
    </svg>
  );
}

export interface CheckoutOverlayProps {
  variant: 'confirming' | 'slow';
  /** e.g. "$120.00 paid via card" */
  amountText: string;
  /** Slow-confirm Done → Telegram close (the webhook + Telegram message are durable). */
  onDone?: () => void;
}

export function CheckoutOverlay({ variant, amountText, onDone }: CheckoutOverlayProps) {
  if (variant === 'slow') {
    return (
      <div className={styles.overlay} role="status" aria-live="polite" data-testid="checkout-overlay-slow">
        <div className={`${styles.icon} ${styles.iconSlow}`}>
          <SpinnerClockIcon />
        </div>
        <div className={styles.title}>{CHECKOUT_CONFIRM_WORDS.title}</div>
        <div className={styles.amount}>{amountText}</div>
        <div className={`${styles.redir} ${styles.redirSlow}`}>{CHECKOUT_CONFIRM_WORDS.slow}</div>
        <button type="button" className={styles.done} onClick={onDone}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div className={styles.overlay} role="status" aria-live="polite" data-testid="checkout-overlay-confirming">
      <div className={styles.icon}>
        <CheckCircleIcon />
      </div>
      <div className={styles.title}>{CHECKOUT_CONFIRM_WORDS.title}</div>
      <div className={styles.amount}>{amountText}</div>
      <div className={styles.redir}>
        <ClockIcon />
        {CHECKOUT_CONFIRM_WORDS.confirming}
      </div>
    </div>
  );
}
